import { Currency } from '~/constants/currency';
import { DistanceUnit } from '~/constants/distance';
import { WeightUnit } from '~/constants/weight';
import pick from 'lodash-es/pick';
import * as moment from 'moment';
import { OnboardingLocalStorageKeys } from '~/pages/Onboarding/onboarding.constants';
import { Activation, UserDraftRequests } from '~/reducers/user/user.reducer';
import { createSelector } from 'reselect';
import { BaseAddress, Country, isAddressIncomplete } from '~/types/Address';
import APIAddress from '~/types/APIAddress';
import { Asset, AssetType } from '~/types/Asset';
import { Brand } from '~/types/Brand';
import LabelFormat, { DefaultLabelFormat } from '~/types/LabelFormat';
import ParcelTemplate from '~/types/ParcelTemplate';
import Promo from '~/types/Promo';
import User, {
  hasValidBilling,
  isHighFraudRisk,
  ProfileUser,
  UserConfigDefaults,
  UserDefaultServiceLevel,
} from '~/types/User';
import { extractRefreshable } from '~/utils/fetchable';
import responsiveCheck from '~/utils/responsiveCheck';
import { isNotNil } from '~/utils/utils';
import { ApplicationState } from '../store';
import * as Cookies from 'js-cookie';
import { isShippoEmail } from '~/utils/validations';
import { FeatureFlagUserData } from '~/utils/featureflagsV2/types';

export const getUserStore = (state: ApplicationState) => state.user;

export const getUser = createSelector(getUserStore, (userStore): Maybe<User> => userStore.user);

export const getUserIsLoading = createSelector(
  getUserStore,
  (userStore): boolean => userStore.isLoading,
);

export const getUserIsAuthenticated = createSelector(
  getUserStore,
  (userStore): boolean => userStore.isAuthenticated,
);

export const getUserId = createSelector(
  getUser,
  (user): Maybe<number> => (user && user.id) || null,
);

export const getUserObjectId = createSelector(
  getUser,
  (user): Maybe<string> => (user && user.objectId) || null,
);

export const getUserAddress = createSelector(
  getUser,
  (user: Maybe<User>): Maybe<APIAddress> => (user ? user.address : null),
);

export const getUserReturnAddress = createSelector(
  getUser,
  (user: Maybe<User>): Maybe<APIAddress> => (user ? user.addressReturn : null),
);

export const getUserCompany = createSelector(getUser, (user: Maybe<User>): string =>
  user && user.address ? user.address.company : '',
);

export const getCompanyName = createSelector(
  getUserStore,
  (userStore): Maybe<string> =>
    userStore.company
      ? userStore.company.companyName
        ? userStore.company.companyName
        : null
      : null,
);

export const isUserLoading = createSelector(
  getUser,
  getUserIsLoading,
  (user, userIsLoading): boolean => userIsLoading || !user,
);

export const getUserName = createSelector(
  getUser,
  (user: Maybe<User>): string => (user && user.firstName) || '',
);

export const getUserFullName = createSelector(getUser, (user: Maybe<User>): string =>
  user && user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : '',
);

export const getUserEmail = createSelector(
  getUser,
  (user: Maybe<User>): Maybe<string> => (user ? user.email : null),
);

export const getUserPhoneNumber = createSelector(
  getUserAddress,
  (address: Maybe<APIAddress>): string => (address && address.phone ? address.phone : ''),
);

export const getUserCountry = createSelector(
  getUserAddress,
  (address: Maybe<APIAddress>): Maybe<Country> => (address ? address.country : null),
);

export const getUserCountryIso2 = createSelector(
  getUserCountry,
  (country: Maybe<Country>): Maybe<string> => (country ? country.iso2 : null),
);

export const getIsUserCountryUS = createSelector(
  getUserCountryIso2,
  (iso2: Maybe<string>): boolean => iso2 === 'US',
);

export const getIsUserCountryCA = createSelector(
  getUserCountryIso2,
  (iso2: Maybe<string>): boolean => iso2 === 'CA',
);

export const getDefaultParcel = createSelector(
  getUser,
  (user: Maybe<User>): Maybe<ParcelTemplate> => (user ? user.defaultParcel : null),
);

export const getDefaultDistanceUnit = createSelector(
  getUser,
  (user: Maybe<User>): Maybe<DistanceUnit> => (user ? user.defaultDistanceUnit : null),
);

export const getDefaultMassUnit = createSelector(
  getUser,
  (user: Maybe<User>): WeightUnit => (user ? user.defaultMassUnit : WeightUnit.LB),
);

export const getDefaultCurrency = createSelector(
  getUser,
  (user: Maybe<User>): Maybe<Currency> => (user ? user.currencyIso : null),
);

export const getDefaultProvider = createSelector(
  getUser,
  (user: Maybe<User>): Maybe<string> => (user ? user.defaultProvider : null),
);

export const getDefaultServiceLevelAccountId = createSelector(
  getUser,
  (user: Maybe<User>): Maybe<string> => (user ? user?.defaultOutboundAccount : null),
);

export const getDefaultServiceLevel = createSelector(
  getUser,
  (user: Maybe<User>): Maybe<string> => (user ? user.defaultServiceLevel : null),
);

export const getDefaultReturnProvider = createSelector(
  getUser,
  (user: Maybe<User>): Maybe<string> => (user ? user.defaultReturnProvider : null),
);

export const getDefaultReturnProviderObjectId = createSelector(
  getUser,
  (user: Maybe<User>): Maybe<string> => (user ? user?.defaultReturnAccount : null),
);

export const getDefaultReturnServiceLevel = createSelector(
  getUser,
  (user: Maybe<User>): Maybe<string> => (user ? user.defaultReturnServiceLevel : null),
);

export const getDefaultLabelFormat = createSelector(getUser, (user: Maybe<User>): LabelFormat => {
  if (user && user.defaultLabelFiletype) {
    return user.defaultLabelFiletype;
  }

  return DefaultLabelFormat;
});

export const getHasDefaultMergedLabels = createSelector(
  getDefaultLabelFormat,
  (labelFormat): boolean =>
    [
      LabelFormat.PDF_W_PSLIP_8x11,
      LabelFormat.PDF_W_PSLIP_COLLATED_4x6,
      LabelFormat.PDF_W_PSLIP_COLLATED_4x8,
      LabelFormat.PDF_W_PSLIP_COLLATED_8x11,
    ].includes(labelFormat),
);

export const getDefaultAutoCreateReturnLabel = createSelector(
  getUser,
  (user: Maybe<User>): Maybe<boolean> => (user ? user.defaultAutoCreateReturnLabel : null),
);

export const getCurrentPromo = createSelector(
  getUser,
  (user: Maybe<User>): Maybe<Promo> => (user ? user.currentPromo : null),
);

export const getUserNeedsOnboarding = createSelector(getUser, (user: Maybe<User>): boolean =>
  user ? !user.isSetup : false,
);

export const getUserConfigDefaults = createSelector(
  getUser,
  getDefaultParcel,
  getDefaultDistanceUnit,
  getDefaultMassUnit,
  getDefaultCurrency,
  getDefaultAutoCreateReturnLabel,
  (
    user,
    defaultParcel,
    defaultDistanceUnit,
    defaultMassUnit,
    defaultCurrency,
    defaultAutoCreateReturnLabel,
  ): Partial<UserConfigDefaults> =>
    user
      ? {
          defaultParcel: defaultParcel!,
          defaultDistanceUnit: defaultDistanceUnit!,
          defaultMassUnit: defaultMassUnit!,
          defaultCurrency: defaultCurrency!,
          defaultAutoCreateReturnLabel: defaultAutoCreateReturnLabel!,
        }
      : {},
);

export const getUserDefaultServiceLevel = createSelector(
  getUser,
  getDefaultProvider,
  getDefaultServiceLevel,
  (user, defaultProvider, defaultServiceLevel): Maybe<{ provider: string; token: string }> =>
    user && defaultProvider && defaultServiceLevel
      ? { provider: defaultProvider, token: defaultServiceLevel }
      : null,
);

export const getUserDefaultServiceLevelExtended = createSelector(
  getUser,
  getDefaultProvider,
  getDefaultServiceLevel,
  getDefaultServiceLevelAccountId,
  (
    user,
    defaultProviderName,
    defaultServiceLevelToken,
    defaultServiceLevelAccountId,
  ): UserDefaultServiceLevel | null => {
    return user && defaultProviderName && defaultServiceLevelToken
      ? {
          provider: defaultProviderName,
          token: defaultServiceLevelToken,
          accountId: defaultServiceLevelAccountId,
        }
      : null;
  },
);

export const getUserDefaultReturnServiceLevel = createSelector(
  getUser,
  getDefaultReturnProvider,
  getDefaultReturnServiceLevel,
  (
    user,
    defaultReturnProvider,
    defaultReturnServiceLevel,
  ): Maybe<{ provider: string; token: string }> =>
    user && defaultReturnProvider && defaultReturnServiceLevel
      ? { provider: defaultReturnProvider, token: defaultReturnServiceLevel }
      : null,
);

export const getUserDefaultReturnServiceLevelExtended = createSelector(
  getUser,
  getDefaultReturnProvider,
  getDefaultReturnServiceLevel,
  getDefaultReturnProviderObjectId,
  (
    user,
    defaultReturnProvider,
    defaultReturnServiceLevel,
    defaultReturnProviderObjectId,
  ): Maybe<{ provider: string; token: string; objectId: string | undefined | null }> =>
    user && defaultReturnProvider && defaultReturnServiceLevel
      ? {
          provider: defaultReturnProvider,
          token: defaultReturnServiceLevel,
          objectId: defaultReturnProviderObjectId,
        }
      : null,
);

export const getIsUserAddressValid = createSelector(
  getUserAddress,
  (address): boolean => address !== null && !isAddressIncomplete(address as BaseAddress),
);

export const getIsUserReturnAddressValid = createSelector(
  getUserReturnAddress,
  (address): boolean => address !== null && !isAddressIncomplete(address as BaseAddress),
);

export const getUserAddressIfValid = createSelector(
  getUserAddress,
  getIsUserAddressValid,
  (userAddress: Maybe<APIAddress>, valid: boolean): Maybe<APIAddress> =>
    valid ? userAddress : null,
);

export const getUserReturnAddressIfValid = createSelector(
  getUserReturnAddress,
  getIsUserReturnAddressValid,
  (userReturnAddress: Maybe<APIAddress>, valid: boolean): Maybe<APIAddress> =>
    valid ? userReturnAddress : null,
);

export const userIsBillingBlocked = createSelector(
  getUser,
  (user: Maybe<User>): boolean => isNotNil(user) && user.blockedBilling,
);

export const userHasValidBilling = createSelector(
  getUser,
  (user) => isNotNil(user) && hasValidBilling(user),
);

export const userIsHighFraudRisk = createSelector(
  getUser,
  (user) => isNotNil(user) && isHighFraudRisk(user),
);

export const userIsLabelDownloadBlocked = createSelector(
  getUser,
  (user): boolean =>
    isNotNil(user) &&
    isHighFraudRisk(user) &&
    moment().diff(user.dateJoined, 'days') < 15 &&
    !user.hasPaidInvoice,
);

export const isStripeAuthorized = createSelector(
  getUser,
  (user): boolean => !!user?.stripeAuthorized,
);

export const isUpdatingCreditCard = createSelector(
  getUserStore,
  (userStore) => userStore.isUpdatingCreditCard,
);

export const getUserCompanyData = createSelector(getUserStore, (userStore) => userStore.company);

export const getUserDateJoined = createSelector(
  getUser,
  (user): Maybe<moment.Moment> => (user ? user.dateJoined : null),
);

export const getUserRevenueTierName = createSelector(
  getUser,
  (user): Maybe<string> => (user ? user.revenueTierName : null),
);

export const isRefreshingUser = createSelector(
  getUserStore,
  (userStore) => userStore.isRefreshingUser,
);

export const showEmailVerificationModal = createSelector(
  getUserStore,
  (userStore) => userStore.showEmailVerificationModal,
);

export const getNextModalAction = createSelector(
  getUserStore,
  (userStore) => userStore.nextModalAction,
);

export const getRequiresEmailVerification = createSelector(getUser, (user): boolean =>
  user ? user.requiresEmailVerification : false,
);

export const getHasFailedPayment = createSelector(getUser, (user): boolean =>
  user ? user.hasFailedPayment : false,
);

export const getIsEligibleForUPSSavings = createSelector(
  getUser,
  (user): boolean =>
    !!user &&
    !!user.savingsCarrierName &&
    user.savingsCarrierName === 'UPS' &&
    !!user.savingsMonthlyAmount,
);

export const getSavingsMonthlyAmount = createSelector(getUser, (user) =>
  user && user.savingsMonthlyAmount ? user.savingsMonthlyAmount : 0,
);

export const getUserAssets = createSelector(
  getUserStore,
  (userStore): Refreshable<Asset[], string> => userStore.assets,
);

export const getUserAssetDraft = createSelector(
  getUserStore,
  (userStore): Requestable<null, string> => userStore.assetDraft,
);

export const getUserAssetDrafts = createSelector(
  getUserStore,
  (userStore): UserDraftRequests => userStore.assetDrafts,
);

export const getIsIDVRequired = createSelector(getUser, (user): boolean => !!user?.isIDVRequired);

export const getUserBrand = createSelector(
  getUserStore,
  (userStore): Maybe<Brand> => userStore.brand,
);

export const getUserLogoAsset = createSelector(getUserAssets, (refreshableAssets): Maybe<Asset> => {
  const assets = extractRefreshable<Asset[], string>(refreshableAssets);
  return assets ? assets.find((asset) => asset.type === AssetType.LOGO) || null : null;
});

export type AssetsByType = {
  [Property in AssetType]: Map<number, Asset>;
};

export const getUserAssetsByType = createSelector(
  getUserAssets,
  (refreshableAssets): AssetsByType => {
    const assets = extractRefreshable<Asset[], string>(refreshableAssets);
    let assetsByType = <AssetsByType>{};
    Object.values(AssetType).forEach((assetType) => {
      assetsByType[assetType] = new Map<number, Asset>();
    });
    if (!assets) {
      return assetsByType;
    }
    assets.forEach((asset) => {
      if (asset && asset?.type && assetsByType[asset.type]) {
        const rank: number = asset?.rank || 1;
        assetsByType[asset.type].set(rank, asset);
      }
    });
    return assetsByType;
  },
);

// If the user's payment type is GOCARDLESS, use German Shippo address on invoice (copied from Ember)
export const getIsGermanShippoAddressInvoice = createSelector(
  getUser,
  (user): boolean => user?.paymentType === 'GOCARDLESS',
);

export const getHasManualPaymentMethod = createSelector(
  getUser,
  (user): boolean => user?.paymentType === 'MANUAL',
);

export const getHasPurchasedLabel = createSelector(
  getUser,
  (user): boolean | undefined => user?.hasPurchasedLabel,
);

export const getActivationData = createSelector(
  getUserStore,
  (userStore): Activation => userStore.activation,
);

export const getUserDataForFeatureTests = createSelector(
  getUser,
  getActivationData,
  (user: Maybe<User>, activationData: Activation): FeatureFlagUserData | null => {
    const isMobile = responsiveCheck();
    const userAgent = navigator.userAgent;

    if (!user) return null;

    const {
      id,
      firstName,
      lastName,
      email,
      revenueTierName,
      isActive,
      dateJoined,
      lifetimeVolSegmentMin,
      lifetimeVolSegmentMax,
      hasPurchasedLabel,
      address,
      currentPromo,
    } = user;
    const dateJoinedParsed = dateJoined.valueOf();
    const country = address && address.country ? address?.country.name : undefined;
    const primaryCarrier = localStorage.getItem('primary_carrier') ?? '';
    const hasPurchasedFirstLabel = localStorage.getItem('has_purchased_first_label') ?? false;
    const userType =
      localStorage.getItem(OnboardingLocalStorageKeys.ONBOARDING_BUSINESS_TYPE) ?? '';
    const registrationStore = localStorage.getItem('registration_store') ?? '';
    const proPlanVariantCookie = Cookies.get('pro_plan_variant') ?? '';

    return {
      id,
      userId: id,
      revenueTierName,
      isActive,
      dateJoined: dateJoinedParsed,
      country: country || '',
      lifetimeVolSegmentMin,
      lifetimeVolSegmentMax,
      hasPurchasedLabel,
      ordersCount: <number>activationData.ordersCount,
      isMobile,
      userAgent,
      primaryCarrier,
      userType,
      registrationStore,
      hasPromo: Boolean(currentPromo),
      hasPurchasedFirstLabel: Boolean(hasPurchasedFirstLabel),
      proPlanVariant: proPlanVariantCookie,
      isShippoEmail: isShippoEmail(email),
      isE2ETestUser:
        (isShippoEmail(email) && email.startsWith('test+')) ||
        (firstName === 'Test' && lastName === 'Cafe'),
      isE2EAutomatedTestUser: isShippoEmail(email) && email.startsWith('test+'),
      isE2EGetStartedUser: isShippoEmail(email) && email.startsWith('test-get-started+'),
    };
  },
);

export const getUserProfile = createSelector(
  getUser,
  (user) =>
    (user
      ? {
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          isAccountOwner: user.isAccountOwner,
          name: `${user.firstName} ${user.lastName}`,
          loginUserId: user.loginUserId,
        }
      : null) as ProfileUser,
);

export const getUserErrorMessage = createSelector(
  getUserStore,
  (userStore) => userStore.userErrorMessage,
);

export const getShouldShowPromoApplicationNotice = createSelector(
  getUserStore,
  (user): boolean => user.showPromoApplicationNotice,
);

export const getUserMetadataForSegment = createSelector(
  getUser,
  (user: Maybe<User>): Partial<User> | null => {
    if (user) {
      return pick(user, [
        'id',
        'loginUserId',
        'email',
        'username',
        'firstName',
        'lastName',
        'address',
        'revenueTierName',
        'blockedBilling',
        'fraudStatus',
        'paymentType',
        'currentPromo',
        'dateJoined',
        'lifetimeVolSegmentMin',
        'lifetimeVolSegmentMax',
        'isIDVRequired',
        'isAccountOwner',
      ]);
    } else {
      return null;
    }
  },
);

export const getUserInsuranceRate = createSelector(getUser, (user): number => {
  return user?.insuranceRate || 1.25;
});

export const getUserInsuranceRateDomestic = createSelector(getUser, (user): number => {
  return user?.insuranceRateDomestic || 1.25;
});

export const getUserInsuranceRateInternaional = createSelector(getUser, (user): number => {
  return user?.insuranceRateInternational || 1.5;
});

export const getCompanySettings = createSelector(getUserStore, (userStore) => userStore.company);

export const getBrandColors = createSelector(getCompanySettings, (company) => [
  company?.brandPrimaryColor,
  company?.brandSecondaryColor,
]);

export const getCompanyIsLoading = createSelector(
  getUserStore,
  (userStore): boolean => userStore.isLoadingCompany,
);

export const getCompanyUpdateRequest = createSelector(
  getUserStore,
  (userStore) => userStore.companyUpdateRequest,
);
